import LinkButton from '@components/utils/cta/linkButton';
import NewTabLink from '@components/utils/links/newTabLink';
import { STORY } from '@constants';
import { TrackEvent } from '@enums';
import {
    OutboundLinkContext,
    OutboundLinkDestinationCategory,
    OutboundLinkShape,
} from '@enums';
import {
    AdditionalProductFragment,
    RedirectionType,
    VariantFragment,
} from '@graphql/generated/graphql';
import { useAnalytics } from '@lib/analytics/analyticsContext';
import { EmbedPostMessageEvent } from '@lib/embed/enums';
import { useEmbedContext } from '@lib/embed/providers/EmbedProvider';
import { EmbedEventAddToCartPayload } from '@lib/embed/types';
import { postMessageToEmbed } from '@lib/embed/utils/postMessageToEmbed';
import { Button, HStack, IconButton } from '@lib/uikit';
import { useTranslation } from 'next-i18next';
import { MouseEventHandler, useCallback, useMemo, useState } from 'react';

enum AddToCartActionStatus {
    STAND_BY = 'STAND_BY',
    DOING = 'DOING',
    FINISHED = 'FINISHED',
}

type PropsType = {
    additionalProduct: AdditionalProductFragment;
    selectedVariant: VariantFragment | null;
    context: OutboundLinkContext;
};

const getAddToCartEventPayload = (
    additionalProduct: AdditionalProductFragment,
    selectedVariant: VariantFragment
): EmbedEventAddToCartPayload => {
    return {
        product: {
            id: additionalProduct.id,
            source: additionalProduct.Source,
        },
        variant: {
            id: selectedVariant.id,
            gtin: selectedVariant.Gtin,
            source: selectedVariant.Source,
        },
    };
};

export const AdditionalProductActions = ({
    additionalProduct,
    selectedVariant,
    context,
}: PropsType) => {
    const { t } = useTranslation(STORY, {
        keyPrefix: 'additional_product.actions',
    });
    const { track } = useAnalytics();
    const { isStoryBundle } = useEmbedContext();

    const [addToCartActionStatus, setAddToCartActionStatus] =
        useState<AddToCartActionStatus>(AddToCartActionStatus.STAND_BY);

    const hasAddToCart = useMemo(
        () =>
            isStoryBundle &&
            additionalProduct.Variants &&
            additionalProduct.Variants.length > 0,
        [isStoryBundle, additionalProduct.Variants]
    );

    const handleAddToCartClick: MouseEventHandler<HTMLButtonElement> =
        useCallback(() => {
            if (!selectedVariant) return;
            setAddToCartActionStatus(AddToCartActionStatus.FINISHED);
            // Send add to cart event
            const actionPayload = getAddToCartEventPayload(
                additionalProduct,
                selectedVariant
            );
            postMessageToEmbed(
                EmbedPostMessageEvent.ADD_TO_CART,
                actionPayload
            );
            // Send analytics event
            track(TrackEvent.OutboundLink, {
                category: 'click',
                destination: additionalProduct.Url || '',
                destination_name: additionalProduct.Name,
                destination_category:
                    OutboundLinkDestinationCategory.AdditionalProduct,
                context: OutboundLinkContext.StoryPage,
                shape: OutboundLinkShape.Button,
                action: EmbedPostMessageEvent.ADD_TO_CART,
                actionPayload: actionPayload,
            });
            // Reset state after cooldown
            setTimeout(() => {
                setAddToCartActionStatus(AddToCartActionStatus.STAND_BY);
            }, 2000);
        }, [additionalProduct, selectedVariant, track]);

    if (!additionalProduct.Url) return null;

    return (
        <>
            {hasAddToCart && (
                <HStack spacing='xs'>
                    <Button
                        isFullWidth
                        rightIcon={
                            addToCartActionStatus ===
                            AddToCartActionStatus.FINISHED
                                ? 'done'
                                : undefined
                        }
                        disabled={
                            !selectedVariant ||
                            addToCartActionStatus ===
                                AddToCartActionStatus.FINISHED
                        }
                        onClick={handleAddToCartClick}
                    >
                        {t('add_to_cart')}
                    </Button>
                    <IconButton
                        as={NewTabLink}
                        ariaLabel='See'
                        icon='open_in_new'
                        color='black'
                        variant='outline'
                        href={additionalProduct.Url}
                        destination_name={additionalProduct.Name}
                        destination_category={
                            OutboundLinkDestinationCategory.AdditionalProduct
                        }
                        context={context}
                        shape={OutboundLinkShape.IconButton}
                    />
                </HStack>
            )}
            {!hasAddToCart && (
                <LinkButton
                    isFullWidth
                    text={t('see')}
                    type={RedirectionType.Outside}
                    url={additionalProduct.Url}
                    destinationName={additionalProduct.Name}
                    destinationCategory={
                        OutboundLinkDestinationCategory.AdditionalProduct
                    }
                    context={context}
                    shape={OutboundLinkShape.Button}
                    rightIcon='open_in_new'
                />
            )}
        </>
    );
};
